import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import orderBy from 'lodash.orderby';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError } from '@/_shared';
import { apiClient, defaultApiParamStr, queryClient } from '@/_shared';

import type {
  SystemUserAllQuerySchema,
  SystemUserQueryLinkerSchema,
  SystemUserQuerySchema,
  SystemUserUpdateSchema,
} from './schemas';

export function getSystemUsersQuery(apiParamStr = defaultApiParamStr) {
  return {
    queryKey: ['SYSTEM_USERS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<SystemUserAllQuerySchema>({
        endpoint: '/api/v1/users',
        params: `${apiParamStr}&include=profile_images,country,roles.permissions`,
      });
      return res;
    },
  };
}

export const useGetUsersMutation: UseControlledMutation<SystemUserQueryLinkerSchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['USERS_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<SystemUserAllQuerySchema>({
            endpoint: '/api/v1/users',
            params: { search, per_page, include: 'profile_images' },
          });

          return orderBy(res?.data ?? [], ['first_name']) || [];
        },
      });
    },
  });

export function useGetUsers(apiParamStr: string) {
  return useSuspenseQuery(getSystemUsersQuery(apiParamStr));
}

export function useGetUser(id: string) {
  return useSuspenseQuery(getSystemUserQuery(id));
}

export function getSystemUserQuery(id: string) {
  return {
    queryKey: ['SYSTEM_USER', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: SystemUserQuerySchema }>({
        endpoint: `/api/v1/users/${id}`,
        params: {
          include: 'profile_images,roles,team_member.profile_image',
        },
        preventAuditLog: true,
      });

      return res;
    },
  };
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: SystemUserQuerySchema }, ApiError, Partial<SystemUserUpdateSchema>>({
    mutationFn: ({ id: bId, ...payload }) =>
      apiClient.request({
        endpoint: `/api/v1/users/${id || bId}`,
        method: 'PUT',
        payload,
      }),
  });
